import type React from "react";
import { useState } from "react";
import { Avatar, AvatarFallback } from "@planta-v2/pds-ui/avatar";
import { PlantaAvatarImage } from "@planta-v2/pds-ui/planta-avatar-image";
import { PlantaMark } from "@frontend/shared/presentation/components/PlantaMark";
import { StringUtils } from "@frontend/shared/presentation/utils/string.utils";
import { resolvePublicAssetUrl } from "@frontend/shared/infrastructure/assets/resolve-public-asset-url";
import { i18n } from "@frontend/i18n";

interface EventHeaderProps {
  readonly eventName: string;

  readonly logoUrl?: string | undefined;
  readonly hasNotification?: boolean;
  readonly onUserIconClick?: (() => void) | undefined;
  readonly viewerPhotoUrl?: string | null;
  readonly viewerDisplayName?: string;
}

export function EventHeader({
  eventName,
  logoUrl,
  hasNotification = false,
  onUserIconClick,
  viewerPhotoUrl = null,
  viewerDisplayName = "",
}: EventHeaderProps): React.JSX.Element {
  const initials = StringUtils.getInitials(viewerDisplayName);
  const resolvedLogoUrl = resolvePublicAssetUrl(logoUrl) ?? undefined;
  // A logo that 404s or is blocked leaves the brand slot empty, so a failed
  // load falls back to the mark rather than to nothing.
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo =
    resolvedLogoUrl !== undefined && resolvedLogoUrl !== "" && !logoFailed;

  return (
    <header className="flex items-start gap-3 px-4 pt-4 pb-4 bg-background">
      {/* Brand slot, title and event name. The event's own logo owns this slot;
          the Planta mark is what stands there when an event has none. */}
      <div className="flex flex-col flex-1 min-w-0">
        {showLogo ? (
          <img
            src={resolvedLogoUrl}
            alt={eventName}
            onError={() => {
              setLogoFailed(true);
            }}
            className="mb-2 max-h-10 w-auto max-w-[120px] self-start object-contain object-left"
          />
        ) : (
          <PlantaMark className="mb-2 h-[15px] w-auto self-start text-foreground" />
        )}
        <h1 className="page-title leading-tight">
          {i18n.t("user.directory.header.title")}
        </h1>
        <span className="text-pds-2xs text-muted-foreground leading-tight truncate">
          {eventName}
        </span>
      </div>

      {/* Account button — the viewer's own photo, initials when they have none */}
      <div className="relative">
        <button
          type="button"
          onClick={onUserIconClick}
          aria-label={i18n.t("user.directory.header.account")}
          className="rounded-full transition-opacity hover:opacity-80"
        >
          <Avatar className="h-10 w-10 rounded-full border border-foreground/10">
            {viewerPhotoUrl !== null && viewerPhotoUrl !== "" ? (
              <PlantaAvatarImage
                src={viewerPhotoUrl}
                alt={viewerDisplayName}
                className="object-cover"
              />
            ) : null}
            <AvatarFallback className="bg-muted-foreground/20 font-serif text-sm font-normal text-foreground/75">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
        {hasNotification && (
          <span className="absolute top-0 right-1 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
        )}
      </div>
    </header>
  );
}
